import esupplyvetApi from "@/api/esupplyvet";
import { useRegisterStore } from "@/stores/register";
import api from "./index";
import { useCartStore } from "@/stores/cartStore";
import apiBase from "@/api/configBasic";
import apiBaseEsupplyvet from "./configBasicEsupplyvet";

function getHttpClient() {
  const registerStore = useRegisterStore();
  const userRole = registerStore.user?.role || "";
  return userRole === "client" ? esupplyvetApi : api;
}

function getApiInstance() {
  const registerStore = useRegisterStore();
  const userRole = registerStore.user?.role || "";
  return userRole === "client" ? apiBaseEsupplyvet : apiBase;
}

/**
 * Fetches all orders for the logged-in user with pagination
 * @param {number} [page=1] - The page number to fetch
 * @param {number} [itemsPerPage=30] - Number of orders per page
 * @returns {Promise} API response containing the list of orders in Hydra format
 */
export function getOrders(page = 1, itemsPerPage = 10, params = {}) {
  const httpClient = getHttpClient();
  return httpClient.get("/orders", {
    headers: { Accept: "application/ld+json" },
    params: { page, itemsPerPage, ...params },
  });
}


/**
 * Fetches full order details by tokenValue
 * @param {string} tokenValue - Unique token value of the order
 * @returns {Promise<Object>} API response with detailed order data
 */
export function getOrderDetails(tokenValue, getfactures = false) {
  const httpClient = getHttpClient();
  const params = getfactures ? { getfactures: true } : {};
  
  return httpClient.get(`/orders/${tokenValue}`, { params });
}


export function getOrderDelegateDetails(tokenValue) {

  return api.get(`/get-detail-order-delegue-veto/${tokenValue}`);



}


/**
 * Fetches full order details by tokenValue
 * @param {string} tokenValue - Unique token value of the order
 * @returns {Promise<Object>} API response with detailed order data
 */
export function getClientsOrderDetails(tokenValue,getfactures=false) {

  const httpClient = getHttpClient();

  const params = getfactures ? { getfactures: true } : {};

  return httpClient.get(`/get-detail-order/${tokenValue}`, { params });



}

/**
 * Fetches all client orders with optional filters and pagination
 * @param {Object} options - Filter and pagination options
 * @param {number} [options.page=1]
 * @param {number} [options.itemsPerPage=30]
 * @param {string|null} [options.status=null]
 * @param {string|null} [options.createdAtAfter=null] - Format: YYYY-MM-DD
 * @param {string|null} [options.createdAtBefore=null] - Format: YYYY-MM-DD
 * @returns {Promise<{ data: Array, totalPages: number }>}
 */
export async function getClientsOrders({
  page = 1,
  itemsPerPage = 10,
  status = null,
  createdAtAfter = null,
  createdAtBefore = null,
} = {}) {
  try {
    const params = { page, itemsPerPage };
    if (status) params.status = status;
    if (createdAtAfter) params["createdAt[after]"] = createdAtAfter;
    if (createdAtBefore) params["createdAt[before]"] = createdAtBefore;
    const httpClient = getHttpClient();

    const response = await httpClient.get("/list-commande-clients", {
      headers: { Accept: "application/ld+json" },
      params,
    });

    return {
      orders: Array.isArray(response.data["hydra:member"]) ? response.data["hydra:member"] : [],
      totalItems: response.data["hydra:totalItems"] || 0,
    };
  } catch (error) {
    console.error("Error fetching client orders:", error);
    throw error;
  }
}

/**
 * Fetches all available order statuses
 * @param {number} [page=1] - The page number to fetch
 * @param {number} [itemsPerPage=30] - Number of statuses per page
 * @returns {Promise<Array>} API response containing the list of statuses
 */
export function getOrderStatuses(page = 1, itemsPerPage = 30) {
  const httpBaseClient = getApiInstance();
  return httpBaseClient.get("/api/v2/order-statuses", {
    params: { page, itemsPerPage },
  });
}

/**
 * Updates an order by tokenValue
 * @param {string} tokenValue
 * @param {Object} payload - The updated order data
 * @param {string} locale - Accept-Language header value (ex: 'fr_FR')
 * @returns {Promise<Object>} API response
 */
export function updateOrder(tokenValue, payload, isDelegue = false, vetId = null) {
  const httpBaseClient = getApiInstance();
  let url = `/api/v2/shop/orders/${tokenValue}`;

  console.log(isDelegue, "isDelegue value in api/orders.js", vetId);
  if (isDelegue && vetId) {
    const params = new URLSearchParams();
    params.append('isDelegate', '1');
    params.append('vetId', vetId);
    url += `?${params.toString()}`;
  }

  return httpBaseClient.put(url, payload);
}

/**
 * Fetch payment method details for each payment in the order
 * @param {Object} order - The order object from API (must include `payments`)
 * @returns {Promise<Array>} List of payment methods
 */
export async function fetchPaymentMethods(order) {
  if (!order || !Array.isArray(order.payments)) return [];

  try {
    const methodLinks = order.payments.map(p => p.method).filter(Boolean);
    const httpBaseClient = getApiInstance();

    const responses = await Promise.all(methodLinks.map(link => httpBaseClient.get(link)));

    return responses.map(res => {
      const data = res.data;
      return {
        id: data.id,
        titre: data.name || "Mode de paiement sans nom",
        description: data.description || "Aucune description disponible",
        instructions: data.instructions || "",
        code: data.code,
        iban: data.iban || "",
        banque: data.banque || "",
        mollie: data.mollie || null, 
      };
    });
  } catch (error) {
    console.error("Erreur lors du chargement des modes de paiement :", error);
    return [];
  }
}

/**
 * Completes an order (PATCH request)
 * @param {string} tokenValue - The order identifier.
 * @param {string} [notes] - Optional notes for the order.
 * @returns {Promise<object>} The response data (the updated Order resource).
 */
export async function completeOrder(tokenValue, notes = "", dateDifferee = null) {
  try {
    const payload = { notes };
    
    // Add shipping date to payload if provided
    if (dateDifferee) {
      payload.dateDifferee = dateDifferee;
    }
    
    const httpClient = getHttpClient();
    const response = await httpClient.patch(`/orders/${tokenValue}/complete`, payload);
    return response.data;
  } catch (error) {
    console.error(`Error completing order ${tokenValue}:`, error);
    throw error;
  }
}

/**
 * Updates the payment method for an order (PATCH request)
 * @param {string} tokenValue - The order identifier
 * @param {string} paymentId - The payment identifier
 * @param {string} paymentMethodCode - The payment method code
 * @param {string} [locale='fr_FR'] - Accept-Language header value
 * @returns {Promise<object>} The response data (the updated Order resource)
 */
export async function updatePaymentMethod(tokenValue, paymentId, paymentMethodCode, shippingMethode) {
  try {
    const cart = useCartStore();
    const httpClient = getHttpClient();
    await updateShipmentShippingMethod(tokenValue, cart.cart.shipments[0].id, shippingMethode);


    const response = await httpClient.patch(`/orders/${tokenValue}/payments/${paymentId}`, {
      paymentMethod:`/api/v2/shop/payment-methods/${paymentMethodCode}`,
    });
    return response.data;
  } catch (error) {
    console.error(`Error updating payment method for order ${tokenValue}:`, error);
    throw error;
  }
}

/**
 * Updates the shipping method for an order
 * @param {string} tokenValue - Order token
 * @param {string} shipmentId - Shipment ID
 * @param {string} shippingMethodIRI - IRI of the selected shipping method (e.g. '/api/v2/shop/shipping-methods/OLD1ID')
 * @param {string} [locale='fr_FR'] - Optional language header
 * @returns {Promise<Object>} Updated order resource
 */
export async function updateShipmentShippingMethod(tokenValue, shipmentId, shippingMethodIRI) {
  const httpClient = getHttpClient();
  
  try {
    let url = `/orders/${tokenValue}/shipments/${shipmentId}`;
    const response = await httpClient.patch(url, {
      shippingMethod: shippingMethodIRI,
    });
    
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour du mode de livraison :", error);
    throw error;
  }
}

/**
 * Submits a relay point for an order (PATCH request)
 * @param {string} tokenValue - The order identifier
 * @param {string} relayId - The relay point identifier
 * @returns {Promise<object>} The response data (the updated Order resource)
 */
export async function submitRelayPoint(tokenValue, relayId) {
  const httpClient = getHttpClient();
  try {
    const response = await httpClient.post(`/orders/${tokenValue}/submit-point-relais`, {
      relay_id: relayId,
    });
    return response.data;
  } catch (error) {
    console.error(`Error submitting relay point for order ${tokenValue}:`, error);
    throw error;
  }
}



export async function submitSameAsComplete(tokenValue, notes = "", dateDifferee = null) {
  try {
    const payload = { notes };
    if (dateDifferee) {
      payload.dateDifferee = dateDifferee;
    }
    const httpClient = getHttpClient();
    const response = await httpClient.post(`/orders/${tokenValue}/submit-same-as-complete`, payload);
    return response.data;
  } catch (error) {
    console.error(`Error in submitSameAsComplete for order ${tokenValue}:`, error);
    throw error;
  }
}

export function createMolliePayment(orderToken) {
  return getHttpClient().post(`/mollie-payments`, { orderToken });
}

export function createMolliePaymentVeto(orderToken, paymentId) {
  return getHttpClient().get(`/orders/${orderToken}/payments/${paymentId}/configuration`);
  
}


/**
 * Update the payment method with Mollie payment type for an order
 * @param {string} orderToken - The order token value
 * @param {string} paymentMethodCode - The code of the payment method (e.g., sylius_mol1)
 * @param {string} molliePaymentMethod - The selected Mollie payment method (e.g., applepay, creditcard)
 * @returns {Promise<void>}
 */
export async function updateMolliePaymentMethod(orderToken, paymentMethodCode, molliePaymentMethod) {
  try {
    const httpBaseClient = getApiInstance();
    await httpBaseClient.put(`/api/v2/shop/orders/${orderToken}/payment-method`, {
      paymentMethodCode,
      molliePaymentMethods: molliePaymentMethod,
    });
  } catch (error) {
    console.error("Erreur lors de la mise à jour du mode de paiement Mollie :", error);
    throw error; 
  }
}


export function exportOrderToDivalto(token) {
  const httpBaseClient = getApiInstance();
return  httpBaseClient.post(`/api/v2/shop/orders/${token}/order-export-divalto`, {}, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
  } 



  export const getClinicAddresses = () => {
    const httpBaseClient = getApiInstance();
  return httpBaseClient.get('/api/v2/shop/addresses-clinique');
};


export async function removePromoCode(tokenValue) {
  const httpBaseClient = getApiInstance();
  try {
    const response = await httpBaseClient.delete(`/api/v2/shop/orders/couponCode/${tokenValue}`);
    return response;
  } catch (error) {
    console.error("Error removing promo code:", error);
    throw error; // Let the caller handle the error
  }
}

/**
 * Supprime une promotion UG (unité gratuite) pour un variant spécifique
 * @param {string} orderToken - Token de la commande
 * @param {string} variantId - ID du variant concerné par la promotion
 * @returns {Promise<Object>} Réponse de l'API
 */
export async function deleteUgPromotion(orderToken, variantId) {
  const httpBaseClient = getApiInstance();
  try {
    const response = await httpBaseClient.delete(`/api/v2/shop/orders/${orderToken}/promotions-ug/variant/${variantId}`);
    return response;
  } catch (error) {
    console.error("Error deleting UG promotion:", error);
    throw error;
  }
}

/**
 * Fetches the warehouse notice configuration
 * @returns {Promise<Object>} The configuration object
 */
export async function getWarehouseNoticeConfiguration() {
  const httpBaseClient = getApiInstance();
  try {
    const response = await httpBaseClient.get("/api/v2/shop/configurations/message_panier_supplyvet_esupplyvet");
    return response.data;
  } catch (error) {
    console.error("Error fetching warehouse notice configuration:", error);
    throw error;
  }
}
